import { FaMap, FaSatellite, FaMountain, FaRoad } from 'react-icons/fa';

/** Unique identifier for the base map layer in OpenLayers */
export const BASE_MAP_LAYER_ID = 'baseMapLayer';

/**
 * Helper to build MapTiler URL with a dynamic API key
 * @param {string} path - MapTiler tile path
 * @param {string|null} apiKey - MapTiler API key
 * @returns {string|null} Full URL or null if key missing
 */
const maptilerUrl = (path, apiKey) =>
	apiKey ? `https://api.maptiler.com/${path}?key=${encodeURIComponent(apiKey)}` : null;

/** Debounce delay (ms) for opacity slider updates */
export const DEBOUNCE_DELAY = 100;

/** Default opacity value for base map layer (0-1) */
export const DEFAULT_OPACITY = 1;

/** Default base map selection on component mount */
export const DEFAULT_MAP = 'default';

/** Preferred base map to switch to once MapTiler key is available */
export const PREFERRED_MAP = 'satellite';

/** Z-index for base map layer positioning */
export const Z_INDEX = 1;

/**
 * Icon sizes used in the selector UI
 * @property {number} sm - Small icon size (option list)
 * @property {number} md - Medium icon size (selector button)
 */
export const ICON_SIZE = {
	sm: 14,
	md: 18,
};

/**
 * Position offsets for the BaseMapSelector component
 * @property {number} default - Bottom offset for orthophoto projects or when slider is collapsed (px)
 * @property {number} withSlider - Bottom offset when single image slider is open (px)
 * @property {number} right - Right offset from viewport edge (px)
 */
export const SELECTOR_POSITION = {
	default: 41 + 8, // Toolbar + gap/margin
	withSlider: 41 + 196 + 8, // Toolbar + single image components + gap/margin
	right: 8, // Gap/margin
};

/**
 * Build base map configurations with a dynamic MapTiler API key
 * @param {string|null} maptilerApiKey - MapTiler API key fetched from the backend
 * @returns {Object.<string, BaseMapConfig>}
 *
 * @typedef {Object} BaseMapConfig
 * @property {string} id - Unique identifier
 * @property {string} name - Display name
 * @property {React.ComponentType|null} icon - Icon component
 * @property {string|null} url - Tile URL template
 * @property {string} [attribution] - Map attribution text
 * @property {boolean} [requiresKey] - Whether this map requires a MapTiler API key
 */
export const getBaseMaps = maptilerApiKey => ({
	default: {
		id: 'default',
		name: 'Default',
		icon: FaMap,
		url: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
		attribution: '© OpenStreetMap contributors',
	},
	satellite: {
		id: 'satellite',
		name: 'Satellite',
		icon: FaSatellite,
		url: maptilerUrl('tiles/satellite-v2/{z}/{x}/{y}.jpg', maptilerApiKey),
		attribution: '© MapTiler © OpenStreetMap contributors',
		requiresKey: true,
	},
	terrain: {
		id: 'terrain',
		name: 'Terrain',
		icon: FaMountain,
		url: maptilerUrl('maps/outdoor-v2/{z}/{x}/{y}.png', maptilerApiKey),
		attribution: '© MapTiler © OpenStreetMap contributors',
		requiresKey: true,
	},
	streets: {
		id: 'streets',
		name: 'Streets',
		icon: FaRoad,
		url: maptilerUrl('maps/streets-v2/{z}/{x}/{y}.png', maptilerApiKey),
		attribution: '© MapTiler © OpenStreetMap contributors',
		requiresKey: true,
	},
	none: {
		id: 'none',
		name: 'None',
		icon: null,
		url: null,
	},
});
